import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import NodeSelect from './NodeSelect';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeConditionRow — Condition row: logic (AND/OR) + key + function + value.
 * Used by: Conditional Path, Conditional Logic, Conditional WF Paths,
 *          Query DB (filter), List Transform (filter/count-if).
 *
 * The caller provides renderKey and renderValue for custom inputs
 * (MentionEditor, ChipInput, DateTimePicker, etc.).
 */

const Root = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  padding: NODE_UI.spacing.md,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  position: 'relative',
  '&:hover .condition-actions': { opacity: 1 },
}));

const ControlsRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
});

const ActionsRow = styled('div')({
  position: 'absolute',
  top: 6,
  right: 6,
  display: 'flex',
  gap: 2,
  opacity: 0,
  transition: 'opacity 0.15s',
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: 3,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.text.primary },
}));

const LOGIC_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
];

const DEFAULT_FUNCTION_OPTIONS = [
  { value: 'EQUAL', label: 'equals' },
  { value: 'NOT_EQUAL', label: 'does not equal' },
  { value: 'MATCHES', label: 'contains' },
  { value: 'NOTMATCH', label: 'does not contain' },
  { value: 'GREATER_THAN', label: 'greater than' },
  { value: 'GREATERTHANOREQUAL', label: 'greater than or equal to' },
  { value: 'LESS_THAN', label: 'less than' },
  { value: 'LESSTHANOREQUAL', label: 'less than or equal to' },
  { value: 'NOTEMPTY', label: 'is not empty' },
  { value: 'EMPTY', label: 'is empty' },
];

const NodeConditionRow = React.memo(function NodeConditionRow({
  logic,
  onLogicChange,
  keyValue,
  onKeyChange,
  functionValue,
  onFunctionChange,
  value,
  onValueChange,
  showLogic = true,
  functionOptions = DEFAULT_FUNCTION_OPTIONS,
  renderKey,
  renderValue,
  onDelete,
  onDuplicate,
  isFirst = false,
  sx,
}) {
  const showValueInput = functionValue !== 'EMPTY' && functionValue !== 'NOTEMPTY';

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <ActionsRow className="condition-actions">
        {onDuplicate && (
          <ActionButton size="small" onClick={(e) => { stopPropagation(e); onDuplicate(); }}>
            <FileCopyOutlinedIcon sx={{ fontSize: 14 }} />
          </ActionButton>
        )}
        {onDelete && (
          <ActionButton size="small" onClick={(e) => { stopPropagation(e); onDelete(); }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </ActionButton>
        )}
      </ActionsRow>

      <ControlsRow>
        {showLogic && !isFirst && (
          <NodeSelect
            value={logic}
            onChange={onLogicChange}
            options={LOGIC_OPTIONS}
            sx={{ maxWidth: 80 }}
          />
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {renderKey
            ? renderKey({ value: keyValue, onChange: onKeyChange })
            : null
          }
        </div>

        <NodeSelect
          value={functionValue}
          onChange={onFunctionChange}
          options={functionOptions}
          placeholder="Function"
          sx={{ minWidth: 130 }}
        />
      </ControlsRow>

      {showValueInput && (
        <div style={{ width: '100%' }}>
          {renderValue
            ? renderValue({ value, onChange: onValueChange })
            : null
          }
        </div>
      )}
    </Root>
  );
});

export default NodeConditionRow;
